import { Message } from '@glif/filecoin-message';
import EventSource from 'eventsource';
import TransportWebUSB from '@ledgerhq/hw-transport-webusb';
import signingTools from '@zondax/filecoin-signing-tools/js';
import FilecoinApp from '@zondax/ledger-filecoin';
import blake from 'blakejs';
import AvatarEight from 'components/Common/Icons/Avatar/AvatarEight';
import AvatarFive from 'components/Common/Icons/Avatar/AvatarFive';
import AvatarFour from 'components/Common/Icons/Avatar/AvatarFour';
import AvatarNine from 'components/Common/Icons/Avatar/AvatarNine';
import AvatarOne from 'components/Common/Icons/Avatar/AvatarOne';
import AvatarSeven from 'components/Common/Icons/Avatar/AvatarSeven';
import AvatarSix from 'components/Common/Icons/Avatar/AvatarSix';
import AvatarTen from 'components/Common/Icons/Avatar/AvatarTen';
import AvatarThree from 'components/Common/Icons/Avatar/AvatarThree';
import AvatarTwo from 'components/Common/Icons/Avatar/AvatarTwo';
import { MockedChartData } from 'components/Pages/Dashboard/DashboardAllowance/DashboardAllowanceInfo/DashboardAllowanceInfoChart/utils';
import { RangePeriod } from 'constants/dashboardChart';
import { API_PATH } from 'constants/general';
import {
  LEDGER_PATH,
  LS_AUTH_DATA_KEY,
  SIGNATURE_TYPE_TO_WALLET_TYPE_MAP,
} from 'constants/user';
import { ReactElement } from 'react';
import {
  Address,
  Allowance,
  ApiUser,
  FilgramFilePayload,
  RewardsPayoutPayload,
  ServerSignatureType,
  SignData,
  UploadedFile,
  UploadedFileStatus,
  User,
  UserWalletType,
} from 'types/user';
import { getMocksEnv, getNodeEnv } from 'utils/env';
import { DEFAULT_AVATAR_CODE } from 'constants/dashboard';
import colors from '../styles/_colors.module.scss';
import { getRequestConfig, handleResponse } from './apiService';
import {
  formatDateAsApiParameter,
  getLocalStorageData,
  letterAlphabetPosition,
  wait,
} from './utilsService';
import { web3StorageClient } from './web3StorageClient';

type VerifySignDataAddress = (
  serverSignatureType: number,
  signData: SignData,
) => Promise<{ token: string; address: string }>;
export const verifySignDataAddress: VerifySignDataAddress = async (
  serverSignatureType,
  signData,
) => {
  const body = JSON.stringify({
    address: signData.address,
    hexMessage: signData.serializedMessage,
    signature: signData.signature,
    signatureType: serverSignatureType,
    skipVerification:
      getNodeEnv() === 'development' || process.env.SKIP_VERIFICATION === 'true',
  });

  const config = getRequestConfig({
    method: 'POST',
    body,
  });

  const res = await fetch(`${API_PATH}/verify`, config).then(handleResponse);

  return { token: res.token, address: signData.address };
};

type LoginViaEmail = (didToken: string) => Promise<string>;
export const loginViaEmail: LoginViaEmail = async (didToken) => {
  const body = JSON.stringify({
    didToken,
  });

  const config = getRequestConfig({
    method: 'POST',
    body,
  });

  const res = await fetch(`${API_PATH}/login`, config).then(handleResponse);

  return res.token;
};

type AddEmail = (didToken: string) => Promise<ApiUser>;
export const addEmail: AddEmail = (didToken) => {
  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);
  if (!localStorageAuthData) throw new Error('Missing auth token');

  const body = JSON.stringify({
    didToken,
  });

  const config = getRequestConfig({
    method: 'POST',
    token: localStorageAuthData.token,
    body,
  });

  return fetch(`${API_PATH}/users/me/emailAddress`, config).then(handleResponse);
};

type AddNewAddressToUser = (
  serverSignatureType: number,
  signData: SignData,
) => Promise<void>;
export const addNewAddressToUser: AddNewAddressToUser = async (
  serverSignatureType,
  signData,
) => {
  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);
  if (!localStorageAuthData) throw new Error('Missing auth token');

  const body = JSON.stringify({
    address: signData.address,
    hexMessage: signData.serializedMessage,
    signature: signData.signature,
    signatureType: serverSignatureType,
    skipVerification:
      getNodeEnv() === 'development' || process.env.SKIP_VERIFICATION === 'true',
  });

  const config = getRequestConfig({
    method: 'POST',
    token: localStorageAuthData.token,
    body,
  });

  return fetch(`${API_PATH}/users/me/address`, config).then(handleResponse);
};

export const getNotificationsEventSource = (): EventSource => {
  const authData = getLocalStorageData(LS_AUTH_DATA_KEY, null);
  if (!authData) throw new Error('Missing auth token');

  return new EventSource(`${API_PATH}/notifications`, {
    headers: { Authorization: `Bearer ${authData.token}` },
  });
};

type GetUser = (token: string) => Promise<ApiUser>;
export const getUser: GetUser = (token) => {
  const config = getRequestConfig({ method: 'GET', token });

  return fetch(`${API_PATH}/users/me`, config).then(handleResponse);
};

/**
 * Update Storage Provider
 *
 * @param data {Object}
 */
type UpdateUserData = (data: {
  email?: string;
  name?: string;
  company?: string;
  avatarCode?: string;
}) => Promise<User>;
export const updateUserData: UpdateUserData = async (data) => {
  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);
  if (!localStorageAuthData) throw new Error('Missing auth token');

  const config = getRequestConfig({
    method: 'PATCH',
    token: localStorageAuthData.token,
    body: JSON.stringify(data),
  });

  return fetch(`${API_PATH}/users/me`, config).then(handleResponse);
};

type GetSerializedAndHexSigningMessage = (validAddress: string) => string;
export const getSerializedAndHexSigningMessage: GetSerializedAndHexSigningMessage = (
  validAddress,
) => {
  const message = new Message({
    to: validAddress,
    from: validAddress,
    nonce: 1,
    value: 1,
    method: 0,
  });

  return signingTools.transactionSerialize(message.toZondaxType());
};

type SignLedger = (
  address: string,
  // left any in order to not waste time with typing external lib
  filecoinApp: any,
) => Promise<{
  serializedMessage: string;
  signature: string;
}>;
const signLedger: SignLedger = async (address, filecoinApp) => {
  const serializedMessage = getSerializedAndHexSigningMessage(address);
  const hexMessage = Buffer.from(serializedMessage, 'hex');
  const hexMessageCID = getCID(hexMessage).toString('hex');

  const res = await filecoinApp.sign(LEDGER_PATH, hexMessage);
  const signature = res.signature_compact.toString('hex');
  const signaturePrefix = address.charAt(1) === '1' ? '01' : '02';

  return {
    serializedMessage: hexMessageCID,
    signature: `${signaturePrefix}${signature}`,
  };
};

const CID_PREFIX = Buffer.from([0x01, 0x71, 0xa0, 0xe4, 0x02, 0x20]);

function getCID(message: ArrayLike<number>): Buffer {
  const blakeCtx = blake.blake2bInit(32);
  blake.blake2bUpdate(blakeCtx, message);
  const hash = blake.blake2bFinal(blakeCtx);

  return Buffer.concat([CID_PREFIX, hash]);
}

type AuthenticateLedger = () => Promise<SignData>;
export const authenticateLedger: AuthenticateLedger = async () => {
  const transport = await TransportWebUSB.create();
  const filecoinApp = new FilecoinApp(transport);

  const response = await filecoinApp.getAddressAndPubKey(LEDGER_PATH);

  if (!response.addrString)
    throw new Error('Please open the Filecoin app on your Ledger device');

  const { signature, serializedMessage } = await signLedger(
    response.addrString,
    filecoinApp,
  );

  await transport.close();

  return {
    address: response.addrString,
    signature,
    serializedMessage,
  };
};

type GetWalletTypeFromSignatureType = (
  signatureType: ServerSignatureType,
) => UserWalletType;
export const getWalletTypeFromSignatureType: GetWalletTypeFromSignatureType = (
  signaturetype,
) => {
  return SIGNATURE_TYPE_TO_WALLET_TYPE_MAP[signaturetype];
};

type GetWalletTypeFromUserData = (
  userData: ApiUser,
  walletAddress: string,
) => UserWalletType | undefined;
export const getWalletTypeFromUserData: GetWalletTypeFromUserData = (
  userData,
  walletAddress,
) => {
  const addressUserObject = userData.addresses.find(
    ({ pubKeyAddress }) => pubKeyAddress === walletAddress,
  );

  return (
    addressUserObject && getWalletTypeFromSignatureType(addressUserObject.signatureType)
  );
};

type getUserDataActiveWallet = (
  walletAddress: string,
  addresses: Address[],
) => Address | undefined;
export const getUserDataActiveWallet: getUserDataActiveWallet = (
  walletAddress,
  addresses,
) => addresses.find((address) => address.pubKeyAddress === walletAddress);

type GetUserAllowances = (token: string) => Promise<Allowance[]>;
export const getUserAllowances: GetUserAllowances = (token) => {
  const config = getRequestConfig({ method: 'GET', token });

  return fetch(`${API_PATH}/users/me/allowances`, config).then(handleResponse);
};

export type RequestAllowanceParams = {
  minerId: string;
  coSignerAddress: string;
  rewardAddress: string;
};

type RequestAllowance = (
  token: string,
  allowanceParams: RequestAllowanceParams,
) => Promise<Allowance>;

export const requestAllowance: RequestAllowance = (token, allowanceParams) => {
  const config = getRequestConfig({
    method: 'POST',
    token,
    body: JSON.stringify(allowanceParams),
  });

  return fetch(`${API_PATH}/users/me/requestAllowance`, config).then(handleResponse);
};

const allowanceUpdates: { [allowanceId: string]: boolean } = {};

type GetAllowance = (
  token: string,
  allowanceId: string,
  currentAllowanceState?: Allowance,
) => Promise<Allowance>;
export const getAllowance: GetAllowance = (token, allowanceId, currentAllowanceState) => {
  if (!getMocksEnv().includes('allowance') || !currentAllowanceState) {
    const config = getRequestConfig({ method: 'GET', token });

    return fetch(`${API_PATH}/users/me/allowances/${allowanceId}`, config).then(
      handleResponse,
    );
  }

  return updateAllowanceMockingly(allowanceId, currentAllowanceState);
};

function updateAllowanceMockingly(allowanceId: string, currentAllowanceState: Allowance) {
  // For each request first return the old state as if the change hasn't been detected
  // on the chain. Only update the state to the next one on second function call.

  const allowanceUpdatedOnPreviousFunctionCall =
    allowanceUpdates[allowanceId] === undefined || allowanceUpdates[allowanceId];
  allowanceUpdates[allowanceId] = !allowanceUpdatedOnPreviousFunctionCall;

  const newAllowanceState = allowanceUpdatedOnPreviousFunctionCall
    ? currentAllowanceState
    : getNextAllowanceState(currentAllowanceState);

  return wait(100, newAllowanceState);
}

function getNextAllowanceState(allowance: Allowance): Allowance {
  switch (allowance.status) {
    case 'PENDING_DEPOSIT': {
      return {
        ...allowance,
        status: 'PENDING_MULTISIG_CREATION',
      };
    }

    case 'PENDING_MULTISIG_CREATION': {
      return {
        ...allowance,
        status: 'PENDING_OWNER_ADDRESS_CHANGE_APPROVAL',
        multisigAddress: {
          IDAddress: 'f099933',
          actorType: 'msig',
          isVerified: true,
          pubKeyAddress: null,
        },
      };
    }

    case 'PENDING_OWNER_ADDRESS_CHANGE_APPROVAL':
      return { ...allowance, status: 'PENDING_OWNER_ADDRESS_CHANGE' };

    case 'PENDING_OWNER_ADDRESS_CHANGE':
      return {
        ...allowance,
        status: 'ACTIVE',
        startedAt: 'startedAt',
        expiresAt: 'expiresAt',
        allowanceAmount: '2500000000000000000000',
        totalAllowancesReleased: '1500000000000000000000',
        depositedAmount: '300000000000000000000',
        totalRewardsPaidOut: '300000000000000000000',
        pledgedAmount: '500000000000000000000',
      };
    default:
      return allowance;
  }
}

type GetRewardsPaidOut = (payload: {
  from?: Date;
  to?: Date;
  range?: RangePeriod;
  offset?: string;
  limit?: number;
}) => Promise<RewardsPayoutPayload>;
export const getRewardsPaidOut: GetRewardsPaidOut = async ({
  from,
  to,
  offset = 0,
  limit = 4,
  range,
}) => {
  if (getMocksEnv().includes('rewards')) {
    const data =
      range === 'LastSevenDays'
        ? MockedChartData.sevenDays
        : range === 'LastThirtyDays'
        ? MockedChartData.thirtyDays
        : MockedChartData.allTime;

    const lastSixResults = {
      ...data,
      results: data.results.slice(data.results.length - limit, data.results.length),
    };
    const payload = wait(500, range ? data : lastSixResults);

    return payload;
  }

  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);
  if (!localStorageAuthData) throw new Error('Missing auth token');
  const config = {
    method: 'GET',
    headers: new Headers({
      Authorization: `Bearer ${localStorageAuthData.token}`,
      'Content-Type': 'application/json',
    }),
  };

  const formatedFromDate = from ? formatDateAsApiParameter(from) : '';
  const formatedToDate = to ? formatDateAsApiParameter(to) : '';

  return fetch(
    `${API_PATH}/users/me/allowances/rewardsPaidOut?from=${formatedFromDate}&to=${formatedToDate}&offset=${offset}${
      limit ? `&limit=${limit}` : ''
    }`,
    config,
  ).then(handleResponse);
};

type uploadFileFilgram = (file: FilgramFilePayload) => Promise<UploadedFile>;
export const uploadFileFilgram: uploadFileFilgram = async (file) => {
  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);
  if (!localStorageAuthData) throw new Error('Missing auth token');

  const config = getRequestConfig({
    method: 'POST',
    token: localStorageAuthData.token,
    body: JSON.stringify(file),
  });

  return fetch(`${API_PATH}/users/me/uploadedFiles`, config).then(handleResponse);
};

type deleteFileFilgram = (cid: string) => Promise<void>;
export const deleteFileFilgram: deleteFileFilgram = async (cid: string) => {
  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);
  if (!localStorageAuthData) throw new Error('Missing auth token');

  const config = getRequestConfig({
    method: 'DELETE',
    token: localStorageAuthData.token,
  });

  return fetch(`${API_PATH}/users/me/uploadedFiles/${cid}`, config).then(handleResponse);
};

type updateFileFilgram = (data: {
  cid: string;
  status: UploadedFileStatus;
}) => Promise<void>;
export const updateFileFilgram: updateFileFilgram = async (data) => {
  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);
  if (!localStorageAuthData) throw new Error('Missing auth token');

  const config = getRequestConfig({
    method: 'PATCH',
    token: localStorageAuthData.token,
    body: JSON.stringify(data),
  });

  return fetch(`${API_PATH}/users/me/uploadedFiles/${data.cid}`, config).then(
    handleResponse,
  );
};

type uploadFileWeb3Storage = (
  file: File,
  onStoredChunk: (chunkSize: number) => void,
) => Promise<string>;
export const uploadFileWeb3Storage: uploadFileWeb3Storage = async (
  file,
  onStoredChunk,
) => {
  return web3StorageClient
    .put([file], { wrapWithDirectory: false, name: file.name, onStoredChunk })
    .then((cid) => cid);
};

type deleteFileWeb3Storage = (cid: string) => Promise<string>;
export const deleteFileWeb3Storage: deleteFileWeb3Storage = async (cid) => {
  return web3StorageClient.delete(cid);
};

export function isUserStorageProvider(userData: User | null): boolean {
  return userData
    ? userData.addresses.some(({ storageProvider }) => storageProvider !== null)
    : false;
}

export function hasAddress(userData: User | null): boolean {
  return userData ? userData.addresses.length > 0 : false;
}

export function isUserFilmineMember(userData: User | null): boolean {
  const addressesWithAccess = process.env.HAS_FILMINE_ACCESS?.split(',');

  return !!userData?.addresses.some((address) =>
    addressesWithAccess?.includes(address.IDAddress),
  );
}

export const getAvatarTypeFromCode = (avatarCode: string): string => avatarCode.charAt(0);

export const getAvatarColorFromCode = (avatarCode: string): string =>
  avatarCode.substr(avatarCode.length - 1);

export const getAvatarFromCode = (avatarCode: string): ReactElement => {
  const avatarType = getAvatarTypeFromCode(avatarCode);
  const avatarColor = (parseInt(getAvatarColorFromCode(avatarCode), 10) % 7).toString();
  let iconBackground = colors['--slate'];
  let iconColor = colors['--body'];

  switch (avatarColor) {
    case '0':
      iconBackground = colors['--green-500'];
      iconColor = colors['--pink-500'];
      break;
    case '1':
      iconBackground = colors['--pink-500'];
      iconColor = colors['--green-500'];
      break;
    case '2':
      iconColor = colors['--pink-500'];
      break;
    case '3':
      iconColor = colors['--green-500'];
      break;
    case '4':
      iconColor = colors['--body'];
      break;
    case '5':
      iconBackground = colors['--green-500'];
      iconColor = colors['--black'];
      break;
    case '6':
      iconBackground = colors['--pink-500'];
      iconColor = colors['--black'];
      break;
  }

  switch (avatarType) {
    case '0':
      return <AvatarOne background={iconBackground} color={iconColor} />;
    case '1':
      return <AvatarTwo background={iconBackground} color={iconColor} />;
    case '2':
      return <AvatarThree background={iconBackground} color={iconColor} />;
    case '3':
      return <AvatarFour background={iconBackground} color={iconColor} />;
    case '4':
      return <AvatarFive background={iconBackground} color={iconColor} />;
    case '5':
      return <AvatarSix background={iconBackground} color={iconColor} />;
    case '6':
      return <AvatarSeven background={iconBackground} color={iconColor} />;
    case '7':
      return <AvatarEight background={iconBackground} color={iconColor} />;
    case '8':
      return <AvatarNine background={iconBackground} color={iconColor} />;
    case '9':
      return <AvatarTen background={iconBackground} color={iconColor} />;
    default:
      return <AvatarTen background={iconBackground} color={iconColor} />;
  }
};

export const getUserShownName = (
  userData: ApiUser | null,
  walletAddress: string | null,
): string => {
  if (!userData) return '';

  if (userData.name) return userData.name;

  const activeAddress = userData?.addresses.find(
    ({ pubKeyAddress }) => pubKeyAddress === walletAddress,
  );

  return activeAddress ? activeAddress.IDAddress : userData?.email.split('@')[0];
};

export const generateUserAvatarCodeFromString = (string: string): string => {
  if (!string) return DEFAULT_AVATAR_CODE;

  const nameFirstCharacter = string.slice(1);
  const nameLastCharacter = string.slice(string.length - 1);

  if (
    Number.isInteger(parseInt(nameFirstCharacter, 10)) &&
    Number.isInteger(parseInt(nameLastCharacter, 10))
  ) {
    return `${nameFirstCharacter.toString()}${nameLastCharacter.toString()}`;
  }

  if (
    !Number.isInteger(parseInt(nameFirstCharacter, 10)) &&
    Number.isInteger(parseInt(nameLastCharacter, 10))
  ) {
    return `${letterAlphabetPosition(
      nameFirstCharacter,
    ).toString()}${nameLastCharacter.toString()}`;
  }

  if (
    Number.isInteger(parseInt(nameFirstCharacter, 10)) &&
    !Number.isInteger(parseInt(nameLastCharacter, 10))
  ) {
    return `${nameFirstCharacter.toString()}${letterAlphabetPosition(
      nameLastCharacter,
    ).toString()}`;
  }

  return `${letterAlphabetPosition(
    nameFirstCharacter,
  ).toString()}${letterAlphabetPosition(nameLastCharacter).toString()}`;
};

export const generateUserAvatarCodeIfNotExist = (
  userData: ApiUser | null,
  walletAddress: string | null,
): string => {
  if (!userData) return DEFAULT_AVATAR_CODE;

  const activeAddress = userData?.addresses.find(
    ({ pubKeyAddress }) => pubKeyAddress === walletAddress,
  );

  return generateUserAvatarCodeFromString(
    activeAddress ? activeAddress.IDAddress : userData?.email.split('@')[0],
  );
};
